import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from "next-themes";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { ftToDisplay, elevUnit, miToDisplay, distUnit } from "@/lib/units";
import { chartColors } from "@/lib/chartTheme";
import { Mountain } from "lucide-react";

// Renders a run's elevation-over-distance profile. `profile` is stored in imperial
// ([{ dist_mi, elev_ft }]); values are converted to the user's unit for display.
export const ElevationChart = ({ profile }) => {
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const c = chartColors(resolvedTheme !== "light");
  if (!Array.isArray(profile) || profile.length < 2) return null;

  const eu = elevUnit(user);
  const du = distUnit(user);
  const data = profile.map((p) => ({
    dist: +miToDisplay(p.dist_mi, user).toFixed(2),
    elev: Math.round(ftToDisplay(p.elev_ft, user)),
  }));

  return (
    <Card className="rounded-2xl bg-card border-border p-4" data-testid="run-elevation-chart">
      <div className="flex items-center gap-2 mb-3">
        <Mountain className="h-4 w-4 text-[#ccff0c]" />
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Elevation profile</p>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 8, left: -14, bottom: 0 }}>
          <defs>
            <linearGradient id="rd-elev-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ccff0c" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#ccff0c" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={c.grid} vertical={false} />
          <XAxis
            dataKey="dist"
            type="number"
            domain={[0, "dataMax"]}
            tick={{ fill: c.tick, fontSize: 10 }}
            tickFormatter={(v) => `${v}`}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: c.tick, fontSize: 10 }}
            width={42}
            domain={["dataMin - 10", "dataMax + 10"]}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{ background: c.tooltipBg, border: `1px solid ${c.tooltipBorder}`, borderRadius: 8 }}
            labelStyle={{ color: c.tooltipLabel, fontSize: 12 }}
            itemStyle={{ color: c.lime }}
            formatter={(value) => [`${value} ${eu}`, "Elevation"]}
            labelFormatter={(label) => `${label} ${du}`}
          />
          <Area type="monotone" dataKey="elev" stroke={c.lime} strokeWidth={2} fill="url(#rd-elev-fill)" dot={false} activeDot={{ r: 3, fill: c.lime }} />
        </AreaChart>
      </ResponsiveContainer>
      <p className="text-muted-foreground text-[11px] mt-2 text-center">Distance ({du}) &middot; Elevation ({eu})</p>
    </Card>
  );
};

export default ElevationChart;

